/**
 * useDocumentDownload Hook
 *
 * React hook for downloading PDF documents with loading and error states
 */

import { useState, useCallback } from 'react';
import { downloadAndSavePdf, openPdfInNewTab, DocumentType } from '@/services/api/documentsService';
import { useCompany } from './useCompany';

/**
 * Document download result
 */
export interface UseDocumentDownloadResult {
  isDownloading: boolean;
  error: Error | null;
  downloadPdf: (
    documentType: DocumentType,
    transactionId: number,
    filename?: string
  ) => Promise<void>;
  openPdfInTab: (documentType: DocumentType, transactionId: number) => Promise<void>;
  clearError: () => void;
}

/**
 * Hook to download PDF documents
 *
 * @returns Document download functions and state
 *
 * @example
 * ```tsx
 * const { downloadPdf, isDownloading, error } = useDocumentDownload();
 *
 * const handleDownload = async () => {
 *   await downloadPdf('transaction-receipt', 123);
 * };
 * ```
 */
export function useDocumentDownload(): UseDocumentDownloadResult {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { company } = useCompany();

  const downloadPdf = useCallback(
    async (documentType: DocumentType, transactionId: number, filename?: string) => {
      if (!company?.companyId) {
        setError(new Error('Company not available'));
        return;
      }

      setIsDownloading(true);
      setError(null);

      try {
        await downloadAndSavePdf(documentType, transactionId, company.companyId, filename);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to download document');
        setError(error);
        console.error('[useDocumentDownload] Download error:', error);
        throw error;
      } finally {
        setIsDownloading(false);
      }
    },
    [company?.companyId]
  );

  const openPdfInTab = useCallback(
    async (documentType: DocumentType, transactionId: number) => {
      if (!company?.companyId) {
        setError(new Error('Company not available'));
        return;
      }

      setIsDownloading(true);
      setError(null);

      try {
        await openPdfInNewTab(documentType, transactionId, company.companyId);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to open document');
        setError(error);
        console.error('[useDocumentDownload] Open error:', error);
        throw error;
      } finally {
        setIsDownloading(false);
      }
    },
    [company?.companyId]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isDownloading,
    error,
    downloadPdf,
    openPdfInTab,
    clearError,
  };
}
